import React, { useRef, useCallback } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * TiltCard — Wrapper JS qui applique un léger tilt 3D suivant la souris.
 * - `max` : angle maximum (degrés). 6 par défaut = subtil, adapté à un CRM.
 * - `perspective` : distance perspective en px.
 * - Respect `prefers-reduced-motion` : fallback pass-through.
 *
 * Usage : <TiltCard as="div" className="..."> ... </TiltCard>
 */
export function TiltCard({
  as: Tag = 'div',
  max = 6,
  perspective = 900,
  className = '',
  children,
  ...rest
}) {
  const ref = useRef(null);
  const reduced = useReducedMotion();

  const onMove = useCallback((e) => {
    const node = ref.current;
    if (!node || reduced) return;
    const rect = node.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;   // 0..1
    const py = (e.clientY - rect.top) / rect.height;   // 0..1
    const rx = ((py - 0.5) * -2 * max).toFixed(2);
    const ry = ((px - 0.5) *  2 * max).toFixed(2);
    node.style.transform = `perspective(${perspective}px) rotateX(${rx}deg) rotateY(${ry}deg)`;
  }, [max, perspective, reduced]);

  const onLeave = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    node.style.transform = `perspective(${perspective}px) rotateX(0deg) rotateY(0deg)`;
  }, [perspective]);

  return (
    <Tag
      ref={ref}
      className={`motion-tilt ${className}`}
      onMouseMove={reduced ? undefined : onMove}
      onMouseLeave={reduced ? undefined : onLeave}
      {...rest}
    >
      {children}
    </Tag>
  );
}
